import { useSyncExternalStore } from "react";
/**
 * What a query answers where there is no query engine to ask: never, and no
 * change to subscribe to. The DOM read below answers the same thing before a
 * media query has been evaluated, so the two agree.
 */ const NEVER_MATCHES = {
    subscribe: ()=>()=>{},
    getSnapshot: ()=>false
};
/**
 * One `MediaQueryList` — and one stable pair of store functions — per query,
 * for the life of the module.
 *
 * `getSnapshot` runs at least once per render per hook instance, and these
 * hooks are read per mention chip inside a transcript that re-renders on every
 * streamed token: calling `window.matchMedia()` there would allocate a fresh
 * listener target that many times, where the cached one makes the read a field
 * access. The functions are cached with it because identities that changed per
 * render would make React tear the subscription down and set it up again just
 * as often.
 *
 * The cache is written during render and there is no way to clear it: a test
 * that needs a different answer must stub the HOOK, because a `matchMedia` stub
 * installed after the first render of a query is never read — and one that
 * matched every query would also claim reduced motion.
 */ const storesByQuery = new Map();
function mediaQueryStore(query) {
    if (typeof window === "undefined" || !window.matchMedia) return NEVER_MATCHES;
    const cached = storesByQuery.get(query);
    if (cached) return cached;
    const mql = window.matchMedia(query);
    const store = {
        subscribe: (onChange)=>{
            mql.addEventListener("change", onChange);
            return ()=>mql.removeEventListener("change", onChange);
        },
        getSnapshot: ()=>mql.matches
    };
    storesByQuery.set(query, store);
    return store;
}
// No SSR in this app, so this is never consulted — but `useSyncExternalStore`
// throws without it in exactly the window-less environment `NEVER_MATCHES`
// covers, and it must answer what that store answers there.
const getServerSnapshot = ()=>false;
export function useMediaQuery(query) {
    const { subscribe, getSnapshot } = mediaQueryStore(query);
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

//# sourceMappingURL=useMediaQuery.js.map